import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  FileText,
  Archive,
  Settings,
  LogOut,
  Menu,
  X,
  Scan,
  User,
  ChevronDown,
  Package,
  Eye,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ALL_NAV_ITEMS = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard", adminOnly: true },
  { path: "/form/new", icon: FileText, label: "Neues Formular", adminOnly: true },
  { path: "/archive", icon: Archive, label: "Archiv", adminOnly: false },
  { path: "/inventory", icon: Package, label: "Bestand", adminOnly: false },
  { path: "/settings", icon: Settings, label: "Einstellungen", adminOnly: true },
];

export default function DashboardLayout({ children, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { isViewer, username } = useAuth();
  const navItems = ALL_NAV_ITEMS.filter((item) => !isViewer || !item.adminOnly);
  const displayName = isViewer ? "Viewer" : "Admin";
  const roleLabel = isViewer ? "Nur Lesezugriff" : "Administrator";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)' }}>
                <Scan className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#0077B6' }}>
                  sanza Serial Form
                </h1>
              </div>
            </div>
            <button
              className="lg:hidden p-2 text-slate-500 hover:text-slate-700"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path === "/form/new" && location.pathname.startsWith("/form"));
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? "text-white font-medium"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  style={isActive ? { background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)' } : {}}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-slate-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="user-menu-button"
                  className="w-full justify-between h-auto py-3 px-3 hover:bg-slate-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center">
                      {isViewer ? (
                        <Eye className="w-4 h-4 text-slate-600" />
                      ) : (
                        <User className="w-4 h-4 text-slate-600" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-900">{displayName}</p>
                      <p className="text-xs text-slate-500">{roleLabel}</p>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {!isViewer && (
                  <>
                    <DropdownMenuItem asChild>
                      <NavLink to="/settings" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        Einstellungen
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  data-testid="logout-button"
                  className="text-red-600 cursor-pointer"
                  onClick={onLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-6">
          <button
            className="lg:hidden p-2 -ml-2 mr-4 text-slate-500 hover:text-slate-700"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
